import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { BookOpen, Search, Plus, Pencil, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { useDashboardStore } from '../../store/dashboardStore';
import { useMobileEdgeSwipeBack } from '../../hooks/useMobileEdgeSwipeBack';
import {
  insertClinicSpeechTerm,
  updateClinicSpeechTerm,
  deleteClinicSpeechTerm,
} from '../../lib/api';
import type { ClinicSpeechTerm } from '../../types';

type DictionaryRow = {
  rowKey: string;
  phrase: string;
  reading: string;
  term: ClinicSpeechTerm;
};

interface SpeechDictionaryPanelProps {
  isMobile?: boolean;
}

export const SpeechDictionaryPanel: React.FC<SpeechDictionaryPanelProps> = ({ isMobile = false }) => {
  const {
    clinicSpeechTerms,
    refreshClinicSpeechTerms,
    currentClinicId,
    currentUser,
    setShowSpeechDictionaryPanel,
    isNavigatingBack,
    setIsNavigatingBack,
  } = useDashboardStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [newPhrase, setNewPhrase] = useState('');
  const [newReading, setNewReading] = useState('');
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editPhrase, setEditPhrase] = useState('');
  const [editReading, setEditReading] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<ClinicSpeechTerm | null>(null);
  const [deleting, setDeleting] = useState(false);

  // 辞書は initialize で既に読み込む。マウントのたびに refresh すると 404 が連打されるだけなので呼ばない。

  useEffect(() => {
    if (!feedback) return;
    const t = setTimeout(() => setFeedback(null), 3000);
    return () => clearTimeout(t);
  }, [feedback]);

  const rows = useMemo((): DictionaryRow[] => {
    return [...clinicSpeechTerms]
      .sort((a, b) => a.phrase.localeCompare(b.phrase, 'ja'))
      .map((term) => ({
        rowKey: term.id,
        phrase: term.phrase,
        reading: term.reading,
        term,
      }));
  }, [clinicSpeechTerms]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) => r.phrase.toLowerCase().includes(q) || r.reading.toLowerCase().includes(q)
    );
  }, [rows, searchQuery]);

  const handleAdd = async () => {
    const phrase = newPhrase.trim();
    const reading = newReading.trim();
    if (!phrase || !reading) {
      setFeedback({ type: 'error', text: '語句とよみの両方を入力してください' });
      return;
    }
    if (!currentClinicId) {
      setFeedback({ type: 'error', text: '医院情報が取得できません' });
      return;
    }
    setSaving(true);
    const created = await insertClinicSpeechTerm(phrase, reading, currentClinicId, currentUser?.id ?? null);
    setSaving(false);
    if (created) {
      setNewPhrase('');
      setNewReading('');
      setFeedback({ type: 'success', text: '追加しました' });
      await refreshClinicSpeechTerms();
    } else {
      setFeedback({ type: 'error', text: '追加に失敗しました' });
    }
  };

  const startEdit = (row: ClinicSpeechTerm) => {
    setIsNavigatingBack(false);
    setEditingId(row.id);
    setEditPhrase(row.phrase);
    setEditReading(row.reading);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditPhrase('');
    setEditReading('');
  };

  const saveEdit = async () => {
    if (!editingId) return;
    const phrase = editPhrase.trim();
    const reading = editReading.trim();
    if (!phrase || !reading) {
      setFeedback({ type: 'error', text: '語句とよみの両方を入力してください' });
      return;
    }
    setSaving(true);
    const ok = await updateClinicSpeechTerm(editingId, { phrase, reading });
    setSaving(false);
    if (ok) {
      cancelEdit();
      setFeedback({ type: 'success', text: '保存しました' });
      await refreshClinicSpeechTerms();
    } else {
      setFeedback({ type: 'error', text: '保存に失敗しました' });
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    setDeleting(true);
    const ok = await deleteClinicSpeechTerm(id);
    setDeleting(false);
    setDeleteTarget(null);
    if (ok) {
      if (editingId === id) cancelEdit();
      setFeedback({ type: 'success', text: '削除しました' });
      await refreshClinicSpeechTerms();
    } else {
      setFeedback({ type: 'error', text: '削除に失敗しました' });
    }
  };

  const handleBack = useCallback(() => {
    setIsNavigatingBack(true);
    setShowSpeechDictionaryPanel(false);
  }, [setIsNavigatingBack, setShowSpeechDictionaryPanel]);

  const panelVariants: Variants = isMobile
    ? {
        hidden: { x: '100%' },
        visible: { x: 0 },
      }
    : {
        hidden: { x: '-100%' },
        visible: { x: 0 },
      };

  const swipeRef = useRef<HTMLDivElement>(null);
  const { swipeStyle } = useMobileEdgeSwipeBack(swipeRef, {
    isMobile: !!isMobile,
    enabled: !!isMobile,
    onNavigateBack: handleBack,
  });

  const content = (
    <div className="h-full min-w-0 flex flex-col overflow-hidden bg-white border-r">
      <div className="flex items-center justify-between p-4 border-b gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <BookOpen className="w-5 h-5 text-teal-600 shrink-0" aria-hidden />
          <h2 className="text-lg font-semibold text-gray-900 truncate">音声用語辞書</h2>
        </div>
      </div>

      <p className="text-base text-gray-900 px-4 pt-3 pb-1 leading-normal">
        この医院で使用するオリジナルの辞書登録が可能です。
      </p>

      {feedback && (
        <div
          className={`mx-4 mt-2 text-sm px-3 py-2 rounded border ${
            feedback.type === 'success'
              ? 'bg-green-50 text-green-800 border-green-200'
              : 'bg-red-50 text-red-800 border-red-200'
          }`}
        >
          {feedback.text}
        </div>
      )}

      <div className="px-4 pt-2 pb-4 border-b space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="語句・よみで検索"
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-sm text-sm"
            aria-label="検索"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input
            type="text"
            value={newPhrase}
            onChange={(e) => setNewPhrase(e.target.value)}
            placeholder="語句（表示・カルテ用）"
            className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm"
            aria-label="新規語句"
          />
          <input
            type="text"
            value={newReading}
            onChange={(e) => setNewReading(e.target.value)}
            placeholder="よみ（音声認識用）"
            className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm"
            aria-label="新規よみ"
          />
        </div>
        <Button type="button" variant="cta" disabled={saving} onClick={() => void handleAdd()}>
          <Plus className="w-4 h-4" />
          追加
        </Button>
      </div>

      <div className="flex-1 min-w-0 overflow-x-hidden overflow-y-auto p-2">
        <AnimatePresence initial={false}>
          {filtered.map((row) => {
            const isEditingRow = editingId === row.term.id;
            return (
            <motion.div
              key={row.rowKey}
              layout={!isEditingRow}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mb-2 min-w-0 max-w-full border border-gray-200 rounded-sm p-3 bg-white overflow-hidden"
            >
              {isEditingRow ? (
                <div className="space-y-2 min-w-0 w-full">
                  <input
                    type="text"
                    value={editPhrase}
                    onChange={(e) => setEditPhrase(e.target.value)}
                    className="w-full min-w-0 max-w-full box-border px-3 py-2 border border-gray-300 rounded-sm text-sm"
                    aria-label="編集語句"
                  />
                  <input
                    type="text"
                    value={editReading}
                    onChange={(e) => setEditReading(e.target.value)}
                    className="w-full min-w-0 max-w-full box-border px-3 py-2 border border-gray-300 rounded-sm text-sm"
                    aria-label="編集よみ"
                  />
                  <div className="flex gap-2 flex-wrap">
                    <Button type="button" variant="cta" disabled={saving} onClick={() => void saveEdit()}>
                      保存
                    </Button>
                    <Button type="button" variant="ctaMuted" disabled={saving} onClick={cancelEdit}>
                      キャンセル
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-medium text-gray-900 truncate">{row.phrase}</span>
                    </div>
                    <div className="text-sm text-gray-500 mt-0.5 truncate">{row.reading}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => startEdit(row.term)}
                        className="p-2 rounded-sm text-gray-600 hover:bg-gray-100"
                        aria-label="編集"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(row.term)}
                        className="p-2 rounded-sm text-gray-600 hover:bg-gray-100"
                        aria-label="削除"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                </div>
              )}
            </motion.div>
            );
          })}
        </AnimatePresence>
        {filtered.length === 0 && (
          <p className="text-sm text-gray-500 text-center py-8 px-4">
            {searchQuery.trim()
              ? '該当する語句がありません。'
              : 'まだ登録がありません。上のフォームから追加してください。'}
          </p>
        )}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>本当に削除しますか？</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget ? `「${deleteTarget.phrase}」を削除します。この操作は取り消せません。` : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>キャンセル</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void confirmDelete()}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              削除する
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

  if (isMobile) {
    return (
      <div
        ref={swipeRef}
        style={swipeStyle}
        className="fixed top-0 left-0 right-0 bottom-0 pt-[var(--header-actual,var(--header-total))] z-[42]"
      >
        <motion.div
          initial={isNavigatingBack ? { x: '-100%', opacity: 1 } : { x: '100%', opacity: 1 }}
          animate={{ x: 0, opacity: 1 }}
          exit={
            isNavigatingBack
              ? { x: '100%', opacity: 0, transition: { duration: 0.35, ease: 'easeOut' } }
              : { x: '-100%' }
          }
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="h-full w-full min-w-0 overflow-hidden bg-white flex flex-col shadow-lg"
        >
          {content}
        </motion.div>
      </div>
    );
  }

  return (
    <motion.div
      variants={panelVariants}
      initial="hidden"
      animate="visible"
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className="panel-col panel-col--settings z-[31] shadow-lg min-w-0 overflow-hidden"
    >
      {content}
    </motion.div>
  );
};
